import React from 'react';
import { CBadge } from '@coreui/react-pro';
import { CourierState, FleetDelivery } from '../../types/fleet';

export const COURIER_STATES: Record<CourierState, { label: string; color: string }> = {
    available: { label: 'Libre', color: '#10B981' },
    busy: { label: 'En pedido', color: '#3B82F6' },
    no_balance: { label: 'Sin saldo', color: '#F59E0B' },
    offline: { label: 'Desconectado', color: '#94A3B8' },
    blocked: { label: 'Bloqueado', color: '#EF4444' },
};

export const CourierStateBadge: React.FC<{ state: CourierState }> = ({ state }) => {
    const { label, color } = COURIER_STATES[state] ?? COURIER_STATES.offline;
    return (
        <CBadge className="fw-semibold" style={{ backgroundColor: color + '22', color, border: `1px solid ${color}55` }}>
            ● {label}
        </CBadge>
    );
};

export const PAYMENT_LABELS: Record<FleetDelivery['payment'], string> = {
    cash: 'Efectivo',
    app: 'Pagado en la app',
    mixed: 'Efectivo + saldo',
};

// Semáforo de retraso según el estado: [minutos para amarillo, minutos para rojo]
const DELAY_THRESHOLDS: Record<number, [number, number]> = {
    1: [3, 5],   // sin domiciliario
    2: [15, 25], // asignada sin recoger
    3: [30, 45], // en camino
};

export const delayLevel = (delivery: FleetDelivery): 'ok' | 'warning' | 'late' => {
    const thresholds = DELAY_THRESHOLDS[delivery.status_id];
    const minutes = delivery.minutes_in_status ?? 0;
    if (!thresholds) return 'ok';
    if (minutes >= thresholds[1]) return 'late';
    if (minutes >= thresholds[0]) return 'warning';
    return 'ok';
};

export const DelayIndicator: React.FC<{ delivery: FleetDelivery }> = ({ delivery }) => {
    const level = delayLevel(delivery);
    const color = level === 'late' ? '#EF4444' : level === 'warning' ? '#F59E0B' : '#10B981';
    return (
        <span className="d-inline-flex align-items-center gap-1 text-nowrap" title="Tiempo en el estado actual">
            <span className="rounded-circle d-inline-block" style={{ width: 8, height: 8, backgroundColor: color }} />
            <span className={level === 'late' ? 'text-danger fw-semibold' : 'text-body'}>{delivery.minutes_in_status ?? 0} min</span>
        </span>
    );
};

// Se puede asignar o reasignar mientras no se haya recogido, y si no la tiene un domiciliario de otra flota
export const canAssign = (delivery: FleetDelivery) =>
    (delivery.status_id === 1 && !delivery.courier) || (delivery.status_id === 2 && !!delivery.courier?.is_own);

export const formatMinutes = (minutes: number | null) => {
    if (minutes === null || minutes === undefined) return '—';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    return hours < 24 ? `${hours} h` : `${Math.floor(hours / 24)} d`;
};
